/**
 * La cadena de dependencias de la deuda de datos: **qué destraba qué**.
 *
 * Es el bloque que convierte "faltan 6 datos" en "cargá este primero". Cada
 * cadena se lee de izquierda a derecha: el insumo que hay que ir a buscar → la
 * métrica que se carga con ese insumo → las métricas que el panel puede calcular
 * en cuanto exista. Arriba, la palanca: cuál de todas rinde más hoy.
 *
 * Las derivadas (LTV:CAC, Payback) no ofrecen "cargalo acá": su acción es cargar
 * el insumo, no el resultado. Ofrecer la carga de un número que el panel calcula
 * es cómo se terminan escribiendo a mano dos versiones del mismo dato.
 */
import { formatMetricValue, listaEs } from '@ops/contracts'

import { Button, Card, Icon, Panel, Stack, StatusPill } from '../../ui'
import { formatInsumoPct, type Cadena, type Palanca } from './dependencias'

export interface CadenaDeDependenciasProps {
  cadenas: readonly Cadena[]
  palanca: Palanca
  /** Insumos publicados por la API, para poder recalcular a mano. */
  bases: {
    arpu: number | null
    mrrPorCliente: number | null
    clientesActivos: number | null
    churnMensualPct: number | null
    cac: number | null
  }
  onCargar: (slug: string) => void
}

export function CadenaDeDependencias({
  cadenas,
  palanca,
  bases,
  onCargar,
}: CadenaDeDependenciasProps) {
  const slugPalanca = palanca.slugAccion
  const nombrePalanca =
    slugPalanca === null
      ? null
      : (cadenas.find((c) => c.base.slug === slugPalanca)?.base.nombre ??
        slugPalanca)

  return (
    <Panel
      titulo="Cadena de dependencias"
      subtitulo="Qué insumo falta, qué métrica habilita y qué se calcula solo después"
    >
      <Stack gap="md">
        <Card
          padding="sm"
          severidad={palanca.resuelto ? 'ok' : 'warning'}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h4
                data-testid="palanca-titulo"
                className="inline-flex items-center gap-1.5 text-titulo text-text"
              >
                <Icon
                  name={palanca.resuelto ? 'ok' : 'atencion'}
                  size={14}
                  className={
                    palanca.resuelto ? 'text-accent-text' : 'text-warning-text'
                  }
                />
                {palanca.titulo}
              </h4>
              <p className="mt-1 max-w-prosa text-meta leading-relaxed text-muted-strong">
                {palanca.texto}
              </p>
            </div>
            {slugPalanca !== null ? (
              <Button
                variante="primario"
                icono="lapiz"
                onClick={() => onCargar(slugPalanca)}
              >
                Cargar {nombrePalanca}
              </Button>
            ) : null}
          </div>
        </Card>

        {cadenas.length === 0 ? (
          <Card padding="sm" hundida punteada>
            <p className="text-meta text-muted-strong">
              No hay ninguna carga manual pendiente: ninguna métrica de la pantalla
              está esperando un insumo.
            </p>
          </Card>
        ) : (
          <Stack as="ul" gap="sm">
            {cadenas.map((cadena) => (
              <EslabonCadena
                key={cadena.base.slug}
                cadena={cadena}
                onCargar={onCargar}
              />
            ))}
          </Stack>
        )}

        <p className="text-micro text-muted-strong">
          Insumos publicados por la API, para poder recalcular a mano: ARPU{' '}
          <b className="font-semibold text-text">
            {formatMetricValue(bases.arpu, 'usd')}
          </b>{' '}
          · MRR por cliente{' '}
          <b className="font-semibold text-text">
            {formatMetricValue(bases.mrrPorCliente, 'usd')}
          </b>{' '}
          · clientes activos{' '}
          <b className="font-semibold text-text">
            {formatMetricValue(bases.clientesActivos, 'count')}
          </b>{' '}
          · churn mensual{' '}
          {/* Con el formateador de tarjetas esto imprimía 0,6% y la fórmula del
              churn anual —publicada tres bloques más arriba— daba 7,0% contra
              el 6,6% que la misma pantalla muestra. Un insumo se publica con los
              decimales que la cuenta necesita. */}
          <b className="font-semibold text-text">
            {formatInsumoPct(bases.churnMensualPct)}
          </b>{' '}
          · CAC{' '}
          <b className="font-semibold text-text">
            {bases.cac === null ? 'sin dato' : formatMetricValue(bases.cac, 'usd')}
          </b>
          .
        </p>
      </Stack>
    </Panel>
  )
}

function EslabonCadena({
  cadena,
  onCargar,
}: {
  cadena: Cadena
  onCargar: (slug: string) => void
}) {
  const { base, hijos, cierra } = cadena

  return (
    <Card as="li" padding="sm" hundida>
      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[minmax(0,1fr)_18px_minmax(0,240px)_18px_minmax(0,1fr)]">
        {/* 1. El insumo que hay que ir a buscar. */}
        <div className="min-w-0">
          <p className="rotulo mb-1">Falta traer</p>
          <p className="text-meta font-medium text-text">
            {base.insumo ?? (base.metric.formula ?? 'el dato')}
          </p>
          <p className="mt-0.5 text-micro text-muted-strong">{base.motivo}</p>
        </div>

        <Flecha />

        {/* 2. La métrica que se carga con ese insumo. */}
        <div className="min-w-0 rounded border border-dashed border-n300 bg-surface p-2">
          <p className="text-meta font-semibold text-text">{base.nombre}</p>
          <p className="mt-0.5 inline-flex items-center gap-1.5 text-micro font-semibold text-muted-strong">
            <Icon name="sin-dato" size={11} />
            Sin dato
          </p>
          <div className="mt-1.5">
            <Button
              variante="chico"
              icono="mas"
              onClick={() => onCargar(base.slug)}
            >
              Cargalo acá
              <span className="sr-only"> — {base.nombre}</span>
            </Button>
          </div>
        </div>

        <Flecha />

        {/* 3. Qué se destraba. */}
        <div className="min-w-0">
          <p className="rotulo mb-1">Qué destraba</p>
          {hijos.length === 0 ? (
            <>
              <StatusPill severidad="unknown" size="sm" icono={false}>
                No destraba otras
              </StatusPill>
              <p className="mt-1 text-micro text-muted-strong">
                Se lee sola: ninguna métrica del panel depende de ella.
              </p>
            </>
          ) : (
            <>
              <StatusPill
                severidad="warning"
                size="sm"
                icono={false}
                testId="cadena-cierra"
              >
                {cierra === 1
                  ? 'Cargarlo cierra 1 métrica'
                  : `Cargarlo cierra ${cierra} métricas`}
              </StatusPill>
              <ul className="mt-1.5 flex list-none flex-col gap-1 p-0">
                {hijos.map((hijo) => (
                  <li
                    key={hijo.fila.slug}
                    data-testid="cadena-hijo"
                    className="flex flex-col gap-0.5 rounded border border-dashed border-n300 px-1.5 py-1 text-micro text-muted-strong"
                  >
                    <span className="inline-flex items-center gap-1 font-semibold text-text">
                      <Icon
                        name={hijo.listoAlCargar ? 'ok' : 'sin-vincular'}
                        size={10}
                      />
                      {hijo.fila.nombre}
                    </span>
                    <span>
                      {hijo.listoAlCargar
                        ? `Lo calcula el panel: ${hijo.fila.metric.formula ?? 'sale de sus insumos'}.`
                        : `Le sigue faltando ${listaEs([...hijo.faltanAdemas])}.`}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </Card>
  )
}

/** Decorativa: la relación ya está en el texto de cada columna. */
function Flecha() {
  return (
    <span
      aria-hidden="true"
      className="hidden items-center justify-center pt-5 text-n300 lg:flex"
    >
      <Icon name="chevron" size={14} className="-rotate-90" />
    </span>
  )
}
